import * as React from 'react';
import {AppProvider} from '@toolpad/core/AppProvider';
import type {NavigationPageItem} from '@toolpad/core/AppProvider';
import {DashboardLayout, DashboardSidebarPageItem} from '@toolpad/core/DashboardLayout';
import {useDemoRouter} from '@toolpad/core/internal';
import {NutoUiMain} from '../../components/Dashboard/NutoUiMain';
import {DashboardToolbarActions} from '../../components/Dashboard/DashboardToolbarActions';
import {SidebarFooterAccount} from '../../components/Dashboard/SidebarFooterAccount';
import {useModules} from '../../hooks/useModules';
import {useAuthentication} from '../../hooks/useAuthentication';
import {useSession} from '../../hooks/useSession';
import {useLanguage} from '../../hooks/useLanguage';
import {demoTheme} from '../../config/theme';
import {NavigationProvider} from '../../context/NavigationContext';

interface HomeProps {
    readonly window?: () => Window;
}

export default function Home({window}: Readonly<HomeProps>): React.ReactElement {
    const router = useDemoRouter('/dashboard');
    const {user, token, authentication} = useAuthentication();
    const {language} = useLanguage();
    const {modules, navigation, loading, error} = useModules(token, user, language);
    const session = useSession();

    const demoWindow = window ? window() : undefined;

    // Toolpad's built-in sidebar "selected" highlight (isActive) is computed from getItemPath,
    // which depends on each item's *current* position in the nested navigation tree. NutoUiMain,
    // on the other hand, resolves the rendered page from the raw pathname alone (tree-independent,
    // with a leaf-segment fallback - see NutoUiMain.tsx). If the backend module tree ever reports a
    // different parent chain for a module across refetches (e.g. on token-refresh-triggered module
    // reloads), the page still routes correctly but Toolpad's own highlight can silently desync.
    // Render the sidebar item ourselves and compute "selected" the same tree-independent way we
    // resolve pages, so highlighting can never drift from what's actually on screen.
    const pathSegments = decodeURIComponent(router.pathname).replace(/^\//, '').split('/').filter(Boolean);
    const currentLeafSegment = pathSegments[pathSegments.length - 1];
    // The app boots on '/dashboard' (and '/' resolves to the Home page in NutoUiMain), but the sidebar's
    // Home item carries the module segment 'Home'. Treat the landing route as Home so it is highlighted.
    const isHomeRoute = !currentLeafSegment || currentLeafSegment.toLowerCase() === 'dashboard';
    const ancestorSegments = pathSegments.slice(0, -1);
    const renderPageItem = React.useCallback((item: NavigationPageItem) => {
        const selected = item.children
            // Parent item: highlight (e.g. in mini sidebar) when the active page is one of its descendants.
            ? !!item.segment && ancestorSegments.includes(item.segment)
            // Leaf item: highlight when it matches the active page's own segment.
            : !!item.segment && (item.segment === currentLeafSegment
                || (isHomeRoute && item.segment.toLowerCase() === 'home'));
        return <DashboardSidebarPageItem item={item} selected={selected}/>;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLeafSegment, isHomeRoute, ancestorSegments.join('/')]);

    return (
        <NavigationProvider navigate={router.navigate}>
            <AppProvider
                session={session}
                authentication={authentication}
                navigation={navigation}
                router={router}
                theme={demoTheme}
                window={demoWindow}>
                <DashboardLayout defaultSidebarCollapsed
                                 branding={{title: 'Nuto', logo: ''}}
                                 slots={{toolbarActions: DashboardToolbarActions, sidebarFooter: SidebarFooterAccount}}
                                 renderPageItem={renderPageItem}
                                 sx={{
                                     backgroundColor: 'background.default',
                                 }}>
                    <NutoUiMain pathname={router.pathname} modules={modules} loading={loading} error={error}/>
                </DashboardLayout>
            </AppProvider>
        </NavigationProvider>
    );
}
